import React, { useState, useEffect } from 'react';
import { deliveryFeeConfigAPI, cityAPI, workAreaAPI, adminAPI } from '../../services/api';
import './DeliveryFeeSettings.css';

function DeliveryFeeSettings() {
  const [loading, setLoading] = useState(true);
  const [configs, setConfigs] = useState({ default: [], city: [], region: [], restaurant: [] });
  const [cities, setCities] = useState([]);
  const [workAreas, setWorkAreas] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [rSearch, setRSearch] = useState('');
  const [activeTab, setActiveTab] = useState('default');
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [selectedCity, setSelectedCity] = useState('');
  const [formData, setFormData] = useState({
    scope: 'default',
    city: '',
    workAreaId: '',
    restaurantId: '',
    pricingMode: 'base_km',
    slabs: [],
    baseFee: 20,
    perKmRate: 3,
    minFee: 20,
    maxFee: 100,
    freeDeliveryThreshold: ''
  });

  // Fee preview state
  const [previewDistance, setPreviewDistance] = useState(5);
  const [previewResult, setPreviewResult] = useState(null);

  // Route-distance settings (actual road route vs straight-line)
  const [routeDist, setRouteDist] = useState({ useRouteApi: true, circuityFactor: 1.3, loaded: false });
  const [routeSaving, setRouteSaving] = useState(false);

  useEffect(() => {
    fetchAllData();
    (async () => {
      try {
        const res = await adminAPI.getRouteDistanceSettings();
        const d = res.data?.data || {};
        setRouteDist({ useRouteApi: d.useRouteApi !== false, circuityFactor: d.circuityFactor ?? 1.3, loaded: true });
      } catch (_) { setRouteDist((p) => ({ ...p, loaded: true })); }
    })();
  }, []);

  const saveRouteDistance = async (patch) => {
    setRouteSaving(true);
    try {
      const res = await adminAPI.updateRouteDistanceSettings(patch);
      const d = res.data?.data || {};
      setRouteDist({ useRouteApi: d.useRouteApi !== false, circuityFactor: d.circuityFactor ?? 1.3, loaded: true });
    } catch (e) {
      alert(e.response?.data?.message || 'Failed to update route distance setting');
    } finally { setRouteSaving(false); }
  };

  useEffect(() => {
    if (selectedCity) {
      fetchWorkAreas(selectedCity);
    }
  }, [selectedCity]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [configsRes, citiesRes, restoRes] = await Promise.all([
        deliveryFeeConfigAPI.getAllConfigs(),
        cityAPI.getActiveCities(),
        adminAPI.getAllRestaurants().catch(() => null)
      ]);

      setConfigs({ default: [], city: [], region: [], restaurant: [], ...configsRes.data.data.grouped });
      setCities(citiesRes.data.data.cities || []);
      setRestaurants(restoRes?.data?.data?.restaurants || restoRes?.data?.restaurants || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkAreas = async (city) => {
    try {
      const response = await workAreaAPI.getWorkAreasByCity(city);
      setWorkAreas(response.data.data.workAreas || []);
    } catch (error) {
      console.error('Failed to fetch work areas:', error);
    }
  };

  const handleOpenModal = (config = null) => {
    if (config) {
      setEditingConfig(config);
      setFormData({
        scope: config.scope,
        city: config.city || '',
        workAreaId: config.workArea?._id || '',
        restaurantId: config.restaurant?._id || '',
        pricingMode: config.pricingMode || 'base_km',
        slabs: (config.slabs || []).map(s => ({ minKm: s.minKm, maxKm: s.maxKm, chargeRupees: s.chargeRupees })),
        baseFee: config.baseFee,
        perKmRate: config.perKmRate,
        minFee: config.minFee,
        maxFee: config.maxFee,
        freeDeliveryThreshold: config.freeDeliveryThreshold || ''
      });
      if (config.city) {
        setSelectedCity(config.city);
      }
    } else {
      setEditingConfig(null);
      setFormData({
        scope: activeTab === 'default' ? 'default' : activeTab,
        city: '',
        workAreaId: '',
        restaurantId: '',
        pricingMode: 'base_km',
        slabs: [],
        baseFee: 20,
        perKmRate: 3,
        minFee: 20,
        maxFee: 100,
        freeDeliveryThreshold: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    setSelectedCity('');
    setPreviewResult(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? (value === '' ? '' : parseFloat(value)) : value
    }));
  };

  // ── Km-slab editor handlers (pricingMode === 'slab') ──
  const setSlab = (i, key, val) => setFormData(prev => ({
    ...prev,
    slabs: prev.slabs.map((s, idx) => (idx === i ? { ...s, [key]: val } : s))
  }));
  const addSlab = () => setFormData(prev => {
    const lastMax = prev.slabs.length ? Number(prev.slabs[prev.slabs.length - 1].maxKm) || 0 : 0;
    return { ...prev, slabs: [...prev.slabs, { minKm: lastMax, maxKm: lastMax + 3, chargeRupees: '' }] };
  });
  const removeSlab = (i) => setFormData(prev => ({ ...prev, slabs: prev.slabs.filter((_, idx) => idx !== i) }));

  const handleScopeChange = (e) => {
    const scope = e.target.value;
    setFormData(prev => ({
      ...prev,
      scope,
      city: '',
      workAreaId: '',
      restaurantId: ''
    }));
    setSelectedCity('');
    setWorkAreas([]);
    setRSearch('');
  };

  const handleCityChange = (e) => {
    const city = e.target.value;
    setSelectedCity(city);
    setFormData(prev => ({
      ...prev,
      city,
      workAreaId: ''
    }));
  };

  const handlePreviewFee = async () => {
    try {
      const response = await deliveryFeeConfigAPI.previewFee({
        city: formData.city,
        workAreaId: formData.workAreaId,
        distance: previewDistance
      });
      setPreviewResult(response.data.data);
    } catch (error) {
      console.error('Preview failed:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        scope: formData.scope,
        pricingMode: formData.pricingMode === 'slab' ? 'slab' : 'base_km',
        slabs: (formData.slabs || [])
          .map(s => ({ minKm: parseFloat(s.minKm), maxKm: parseFloat(s.maxKm), chargeRupees: parseFloat(s.chargeRupees) }))
          .filter(s => [s.minKm, s.maxKm, s.chargeRupees].every(Number.isFinite) && s.maxKm >= s.minKm && s.chargeRupees >= 0),
        baseFee: parseFloat(formData.baseFee),
        perKmRate: parseFloat(formData.perKmRate),
        minFee: parseFloat(formData.minFee),
        maxFee: parseFloat(formData.maxFee),
        freeDeliveryThreshold: formData.freeDeliveryThreshold ? parseFloat(formData.freeDeliveryThreshold) : null
      };

      if (payload.pricingMode === 'slab' && payload.slabs.length === 0) {
        alert('Add at least one km slab, or switch to Base + per-km mode.');
        return;
      }

      if (formData.scope === 'city') {
        payload.city = formData.city;
      } else if (formData.scope === 'region') {
        payload.workAreaId = formData.workAreaId;
      } else if (formData.scope === 'restaurant') {
        if (!formData.restaurantId) { alert('Please select a restaurant.'); return; }
        payload.restaurantId = formData.restaurantId;
      }

      if (editingConfig) {
        await deliveryFeeConfigAPI.updateConfig(editingConfig._id, payload);
      } else {
        await deliveryFeeConfigAPI.createConfig(payload);
      }

      handleCloseModal();
      fetchAllData();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save config');
    }
  };

  const handleDelete = async (configId) => {
    if (!window.confirm('Are you sure you want to delete this configuration?')) return;

    try {
      await deliveryFeeConfigAPI.deleteConfig(configId);
      fetchAllData();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete config');
    }
  };

  const handleToggleActive = async (config) => {
    try {
      await deliveryFeeConfigAPI.updateConfig(config._id, { isActive: !config.isActive });
      fetchAllData();
    } catch (error) {
      alert('Failed to update config');
    }
  };

  const renderConfigCard = (config) => (
    <div key={config._id} className={`config-card ${!config.isActive ? 'inactive' : ''}`}>
      <div className="config-header">
        <div className="config-scope">
          {config.scope === 'default' && '🌍 Default'}
          {config.scope === 'city' && `🏙️ ${config.city}`}
          {config.scope === 'region' && `📍 ${config.workArea?.name || 'Region'} (${config.workArea?.city || ''})`}
          {config.scope === 'restaurant' && `🍽️ ${config.restaurant?.name || 'Restaurant'}`}
        </div>
        <div className="config-status">
          <span className={`status-badge ${config.isActive ? 'active' : 'inactive'}`}>
            {config.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>
      </div>

      <div className="config-details">
        <div style={{ marginBottom: 8 }}>
          <span style={{
            display: 'inline-block', padding: '2px 9px', borderRadius: 12, fontSize: 11, fontWeight: 700,
            background: config.pricingMode === 'slab' ? 'rgba(22,163,74,0.12)' : 'rgba(79,70,229,0.12)',
            color: config.pricingMode === 'slab' ? '#15803d' : '#4338ca'
          }}>
            {config.pricingMode === 'slab' ? '📊 Km slabs' : '📏 Base + per-km'}
          </span>
        </div>
        {config.pricingMode === 'slab' ? (
          <div className="fee-grid">
            {(config.slabs || []).length === 0 && (
              <div className="fee-item"><span className="fee-label" style={{ color: '#dc2626' }}>No slabs set</span></div>
            )}
            {(config.slabs || []).map((s, i) => (
              <div className="fee-item" key={i}>
                <span className="fee-label">{s.minKm}–{s.maxKm} km</span>
                <span className="fee-value">₹{s.chargeRupees}</span>
              </div>
            ))}
          </div>
        ) : (
        <div className="fee-grid">
          <div className="fee-item">
            <span className="fee-label">Base Fee</span>
            <span className="fee-value">₹{config.baseFee}</span>
          </div>
          <div className="fee-item">
            <span className="fee-label">Per KM</span>
            <span className="fee-value">₹{config.perKmRate}</span>
          </div>
          <div className="fee-item">
            <span className="fee-label">Min Fee</span>
            <span className="fee-value">₹{config.minFee}</span>
          </div>
          <div className="fee-item">
            <span className="fee-label">Max Fee</span>
            <span className="fee-value">₹{config.maxFee}</span>
          </div>
        </div>
        )}
        {config.freeDeliveryThreshold && (
          <div className="free-delivery-info">
            🎁 Free delivery on orders above ₹{config.freeDeliveryThreshold}
          </div>
        )}
      </div>

      <div className="config-actions">
        <button className="btn btn-sm btn-secondary" onClick={() => handleOpenModal(config)}>
          ✏️ Edit
        </button>
        <button
          className={`btn btn-sm ${config.isActive ? 'btn-warning' : 'btn-success'}`}
          onClick={() => handleToggleActive(config)}
        >
          {config.isActive ? '⏸️ Disable' : '▶️ Enable'}
        </button>
        {config.scope !== 'default' && (
          <button className="btn btn-sm btn-danger" onClick={() => handleDelete(config._id)}>
            🗑️ Delete
          </button>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="delivery-fee-settings">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="delivery-fee-settings">
      <div className="page-header">
        <div>
          <h1>🚚 Delivery Fee Settings</h1>
          <p className="subtitle">Configure delivery fees by region, city, or set global defaults</p>
        </div>
        <button className="btn btn-primary" onClick={() => handleOpenModal()}>
          ➕ Add Configuration
        </button>
      </div>

      {/* Explanation Card */}
      <div className="info-card">
        <h3>📖 How it works</h3>
        <p>
          <strong>Priority Order:</strong> Restaurant → Region (Work Area) → City → Default
        </p>
        <ul>
          <li><strong>Restaurant Config:</strong> Most specific — a single restaurant's own delivery slab, overrides everything below for that restaurant</li>
          <li><strong>Region Config:</strong> Applies to orders within a specific work area</li>
          <li><strong>City Config:</strong> Applies to all orders in a city without region-specific config</li>
          <li><strong>Default Config:</strong> Fallback for all locations without specific settings</li>
        </ul>
        <p className="formula">
          <strong>Fee Formula:</strong> Base Fee + (Distance - 1km) × Per KM Rate (capped between Min and Max)
        </p>
      </div>

      {/* Distance calculation toggle (route vs straight-line) */}
      <div className="info-card" style={{ borderLeft: `4px solid ${routeDist.useRouteApi ? '#10B981' : '#9CA3AF'}` }}>
        <h3>🛣️ Distance calculation</h3>
        <p style={{ margin: '4px 0 12px' }}>
          Delivery fee &amp; range use{' '}
          {routeDist.useRouteApi
            ? <strong style={{ color: '#059669' }}>actual road-route distance</strong>
            : <strong style={{ color: '#B45309' }}>straight-line × circuity factor</strong>}
          . Turn OFF to stop Google Directions API calls (falls back to straight-line × factor everywhere).
        </p>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <button
            className={`btn btn-sm ${routeDist.useRouteApi ? 'btn-success' : 'btn-secondary'}`}
            disabled={routeSaving || !routeDist.loaded}
            onClick={() => saveRouteDistance({ useRouteApi: !routeDist.useRouteApi })}
          >
            {routeSaving ? 'Saving…' : routeDist.useRouteApi ? '● Route distance ON' : '○ Route distance OFF'}
          </button>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <span>Circuity factor (road ÷ straight):</span>
            <input
              type="number" min="1" max="3" step="0.05"
              key={routeDist.circuityFactor}
              defaultValue={routeDist.circuityFactor}
              disabled={routeSaving || !routeDist.loaded}
              onBlur={(e) => { const v = Number(e.target.value); if (v >= 1 && v <= 3 && v !== routeDist.circuityFactor) saveRouteDistance({ circuityFactor: v }); }}
              style={{ width: 72, padding: '6px 8px', borderRadius: 6, border: '1px solid #ccc' }}
            />
          </label>
        </div>
        <p style={{ fontSize: 12, color: '#6B7280', marginTop: 8 }}>
          The factor is used for the fee fallback and the distance shown in restaurant discovery. Default 1.3 (road ≈ 1.3× straight-line).
        </p>
      </div>

      {/* Tabs */}
      <div className="tabs">
        <button
          className={`tab ${activeTab === 'default' ? 'active' : ''}`}
          onClick={() => setActiveTab('default')}
        >
          🌍 Default ({configs.default?.length || 0})
        </button>
        <button
          className={`tab ${activeTab === 'city' ? 'active' : ''}`}
          onClick={() => setActiveTab('city')}
        >
          🏙️ By City ({configs.city?.length || 0})
        </button>
        <button
          className={`tab ${activeTab === 'region' ? 'active' : ''}`}
          onClick={() => setActiveTab('region')}
        >
          📍 By Region ({configs.region?.length || 0})
        </button>
        <button
          className={`tab ${activeTab === 'restaurant' ? 'active' : ''}`}
          onClick={() => setActiveTab('restaurant')}
        >
          🍽️ By Restaurant ({configs.restaurant?.length || 0})
        </button>
      </div>

      {/* Config Cards */}
      <div className="configs-grid">
        {activeTab === 'default' && (
          configs.default?.length > 0 ? (
            configs.default.map(renderConfigCard)
          ) : (
            <div className="empty-state">
              <p>No default configuration found. Create one to set global delivery fees.</p>
              <button className="btn btn-primary" onClick={() => {
                setFormData(prev => ({ ...prev, scope: 'default' }));
                handleOpenModal();
              }}>
                ➕ Create Default Config
              </button>
            </div>
          )
        )}

        {activeTab === 'city' && (
          configs.city?.length > 0 ? (
            configs.city.map(renderConfigCard)
          ) : (
            <div className="empty-state">
              <p>No city-specific configurations. Add one to override default fees for specific cities.</p>
            </div>
          )
        )}

        {activeTab === 'region' && (
          configs.region?.length > 0 ? (
            configs.region.map(renderConfigCard)
          ) : (
            <div className="empty-state">
              <p>No region-specific configurations. Add one to override fees for specific work areas.</p>
            </div>
          )
        )}

        {activeTab === 'restaurant' && (
          configs.restaurant?.length > 0 ? (
            configs.restaurant.map(renderConfigCard)
          ) : (
            <div className="empty-state">
              <p>No restaurant-specific configurations. Add one to give a specific restaurant its own delivery slab that overrides the general/city/region fee.</p>
            </div>
          )
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingConfig ? '✏️ Edit Configuration' : '➕ Add Configuration'}</h2>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Scope</label>
                <select
                  name="scope"
                  value={formData.scope}
                  onChange={handleScopeChange}
                  disabled={editingConfig}
                >
                  <option value="default">🌍 Default (Global)</option>
                  <option value="city">🏙️ City-specific</option>
                  <option value="region">📍 Region-specific (Work Area)</option>
                  <option value="restaurant">🍽️ Restaurant-specific</option>
                </select>
              </div>

              {formData.scope === 'restaurant' && (
                <div className="form-group">
                  <label>Restaurant</label>
                  {!editingConfig && (
                    <input
                      type="text"
                      placeholder="Search restaurants…"
                      value={rSearch}
                      onChange={(e) => setRSearch(e.target.value)}
                      style={{ marginBottom: 8 }}
                    />
                  )}
                  <select
                    name="restaurantId"
                    value={formData.restaurantId}
                    onChange={handleInputChange}
                    required
                    disabled={editingConfig}
                  >
                    <option value="">Select Restaurant</option>
                    {restaurants
                      .filter(r => !rSearch || (r.name || '').toLowerCase().includes(rSearch.toLowerCase()))
                      .map(r => (
                        <option key={r._id} value={r._id}>
                          {r.name}{r.address?.city ? ` · ${r.address.city}` : ''}
                        </option>
                      ))}
                  </select>
                  <small>This restaurant's delivery fee will use the slab/fee below, overriding the general, city &amp; region settings.</small>
                </div>
              )}

              {formData.scope === 'city' && (
                <div className="form-group">
                  <label>City</label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleCityChange}
                    required
                    disabled={editingConfig}
                  >
                    <option value="">Select City</option>
                    {cities.map(city => (
                      <option key={city._id || city.name} value={city.name}>
                        {city.name}, {city.state}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {formData.scope === 'region' && (
                <>
                  <div className="form-group">
                    <label>City</label>
                    <select
                      value={selectedCity}
                      onChange={handleCityChange}
                      required
                      disabled={editingConfig}
                    >
                      <option value="">Select City First</option>
                      {cities.map(city => (
                        <option key={city._id || city.name} value={city.name}>
                          {city.name}, {city.state}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Work Area / Region</label>
                    <select
                      name="workAreaId"
                      value={formData.workAreaId}
                      onChange={handleInputChange}
                      required
                      disabled={!selectedCity || editingConfig}
                    >
                      <option value="">Select Work Area</option>
                      {workAreas.map(area => (
                        <option key={area._id} value={area._id}>
                          {area.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              {/* Pricing mode selector */}
              <div className="form-group">
                <label>Pricing mode</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { k: 'base_km', label: 'Base + per-km' },
                    { k: 'slab', label: 'Km slabs' }
                  ].map(opt => (
                    <button
                      key={opt.k}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, pricingMode: opt.k }))}
                      style={{
                        flex: 1, padding: '10px 12px', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer',
                        border: `1.5px solid ${formData.pricingMode === opt.k ? '#4338ca' : '#E2E8F0'}`,
                        background: formData.pricingMode === opt.k ? '#EEF2FF' : '#fff',
                        color: formData.pricingMode === opt.k ? '#4338ca' : '#64748B'
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <small>{formData.pricingMode === 'slab'
                  ? 'Flat fee per km range. Distance beyond the top slab uses the top slab\'s fee.'
                  : 'Base Fee + (distance − 1km) × Per-KM, capped between Min and Max.'}</small>
              </div>

              {formData.pricingMode === 'base_km' ? (
                <div className="fee-inputs-grid">
                  <div className="form-group">
                    <label>Base Fee (₹)</label>
                    <input type="number" name="baseFee" value={formData.baseFee} onChange={handleInputChange} min="0" step="1" required />
                    <small>Fee for first 1 km</small>
                  </div>
                  <div className="form-group">
                    <label>Per KM Rate (₹)</label>
                    <input type="number" name="perKmRate" value={formData.perKmRate} onChange={handleInputChange} min="0" step="0.5" required />
                    <small>Additional fee per km after 1st km</small>
                  </div>
                  <div className="form-group">
                    <label>Minimum Fee (₹)</label>
                    <input type="number" name="minFee" value={formData.minFee} onChange={handleInputChange} min="0" step="1" required />
                    <small>Floor for delivery fee</small>
                  </div>
                  <div className="form-group">
                    <label>Maximum Fee (₹)</label>
                    <input type="number" name="maxFee" value={formData.maxFee} onChange={handleInputChange} min="0" step="1" required />
                    <small>Cap for delivery fee</small>
                  </div>
                </div>
              ) : (
                <div className="form-group">
                  <label>Km slabs (range → flat fee)</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, fontSize: 12, color: '#64748B', fontWeight: 600, marginBottom: 6 }}>
                    <div>From (km)</div><div>To (km)</div><div>Fee (₹)</div><div></div>
                  </div>
                  {formData.slabs.length === 0 && (
                    <div style={{ color: '#94a3b8', fontSize: 14, marginBottom: 10 }}>No slabs yet — add one below.</div>
                  )}
                  {formData.slabs.map((s, i) => (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                      <input type="number" min="0" step="0.5" value={s.minKm} onChange={e => setSlab(i, 'minKm', e.target.value)} />
                      <input type="number" min="0" step="0.5" value={s.maxKm} onChange={e => setSlab(i, 'maxKm', e.target.value)} />
                      <input type="number" min="0" step="1" value={s.chargeRupees} onChange={e => setSlab(i, 'chargeRupees', e.target.value)} />
                      <button type="button" onClick={() => removeSlab(i)} style={{ background: '#fee2e2', color: '#b91c1c', border: 'none', borderRadius: 8, padding: '8px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Remove</button>
                    </div>
                  ))}
                  <button type="button" onClick={addSlab} style={{ marginTop: 6, background: '#EEF2FF', color: '#4338ca', border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>+ Add slab</button>
                  <small style={{ display: 'block', marginTop: 8 }}>e.g. 0–3 km ₹25 · 3–6 km ₹40 · 6–10 km ₹70. Keep ranges contiguous.</small>
                </div>
              )}

              <div className="form-group">
                <label>Free Delivery Threshold (₹) - Optional</label>
                <input
                  type="number"
                  name="freeDeliveryThreshold"
                  value={formData.freeDeliveryThreshold}
                  onChange={handleInputChange}
                  min="0"
                  step="10"
                  placeholder="Leave empty for no free delivery"
                />
                <small>Orders above this amount get free delivery</small>
              </div>

              {/* Fee Preview */}
              <div className="preview-section">
                <h4>🧮 Fee Preview</h4>
                <div className="preview-input">
                  <label>Distance (km):</label>
                  <input
                    type="number"
                    value={previewDistance}
                    onChange={(e) => setPreviewDistance(parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.5"
                  />
                  <button type="button" className="btn btn-sm btn-secondary" onClick={handlePreviewFee}>
                    Calculate
                  </button>
                </div>
                {previewResult && (
                  <div className="preview-result">
                    <p><strong>Distance:</strong> {previewResult.distance} km</p>
                    <p><strong>Delivery Fee:</strong> ₹{previewResult.deliveryFee}</p>
                    <p className="breakdown">
                      Base ₹{previewResult.breakdown.baseFee} +
                      Distance charge ₹{previewResult.breakdown.distanceCharge} =
                      ₹{previewResult.breakdown.beforeCaps}
                      {previewResult.breakdown.finalFee !== previewResult.breakdown.beforeCaps &&
                        ` (capped to ₹${previewResult.breakdown.finalFee})`}
                    </p>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingConfig ? '💾 Save Changes' : '➕ Create Config'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default DeliveryFeeSettings;
